import cookies from 'js-cookie';

interface IStorageBackend {
  getItem(key: string): string | null | undefined;
  removeItem(key: string): void;
  setItem(key: string, value: string, options?: object): void;
}

function defaultBackend(): IStorageBackend {
  // @ts-ignore — `process.browser` is webpack-defined.
  if (process.browser) {
    try {
      return window.localStorage as unknown as IStorageBackend;
    } catch {
      return new CookieStorage();
    }
  }
  return new DummyStorage();
}

export default class StorageHandler implements IStorageBackend {
  storageBackend: IStorageBackend;
  namespace: string;

  constructor(namespace?: string, storageBackend?: string) {
    this.namespace = namespace ?? '';
    switch (storageBackend) {
      case 'localstorage':
        try {
          this.storageBackend = window.localStorage as unknown as IStorageBackend;
        } catch {
          this.storageBackend = new CookieStorage();
        }
        break;
      case 'cookie':
        this.storageBackend = new CookieStorage();
        break;
      case 'dummy':
        this.storageBackend = new DummyStorage();
        break;
      default:
        this.storageBackend = defaultBackend();
        break;
    }
  }

  getItem(key: string) {
    try {
      return this.storageBackend.getItem(this.getKey(key));
    } catch {
      this.failover();
      return this.storageBackend.getItem(this.getKey(key));
    }
  }

  removeItem(key: string) {
    try {
      this.storageBackend.removeItem(this.getKey(key));
    } catch {
      this.failover();
      this.storageBackend.removeItem(this.getKey(key));
    }
  }

  setItem(key: string, value: string, options?: object) {
    try {
      this.storageBackend.setItem(this.getKey(key), value, options);
    } catch {
      this.failover();
      this.storageBackend.setItem(this.getKey(key), value, options);
    }
  }

  getKey(key: string): string {
    return `${this.namespace}.${key}`;
  }

  failover() {
    if (this.storageBackend instanceof DummyStorage) {
      console.warn('DummyStorage: ignore failover');
    } else if (this.storageBackend instanceof CookieStorage) {
      console.warn('CookieStorage: failing over DummyStorage');
      this.storageBackend = new DummyStorage();
    } else {
      console.warn('LocalStorage: failing over CookieStorage');
      this.storageBackend = new CookieStorage();
    }
  }
}

class CookieStorage implements IStorageBackend {
  getItem(key: string): string | undefined {
    return cookies.get(key);
  }

  removeItem(key: string): void {
    cookies.remove(key);
  }

  setItem(key: string, value: string, options?: object) {
    cookies.set(key, value, options ?? {});
  }
}

class DummyStorage implements IStorageBackend {
  storage: {
    [key: string]: string;
  } = {};

  getItem(key: string): string | null {
    return this.storage[key];
  }

  removeItem(key: string): void {
    delete this.storage[key];
  }

  setItem(key: string, value: string) {
    this.storage[key] = value;
  }
}
